import EmptyPdf from '@/assets/empty.pdf?base64';
import { TErrorNotification } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { useGetApPayeesMacroList } from '@/context/services/doc_instructions/DocInstructions.service';
import { generatePdf } from '@/context/services/generate_pdf/index.service';
import {
  PreviewPDFRequest,
  PreviewPDFResponse,
} from '@/context/services/generate_pdf/types';
import { replaceMacros } from '@/features/ap_payees/components/DocInstructionMenu/Helpers/replaceMarcro';
import { InstructionPdfForm } from '@/features/ap_payees/components/DocInstructionMenu/types';
import { useDebouncedCallback } from '@/hooks/useDebounce';
import { isCommonError } from '@/libs/typeguard/isError';
import { escapeHtml } from '@/utils/escapeHtml';
import { useMutation } from '@tanstack/react-query';
import { DATE_FORMAT_JA } from 'date-util';
import dayjs from 'dayjs';
import { useCreateObjectURLWithMemo, useToBlob } from 'packages-hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

export const usePreviewFile = (
  apPayeeId: string,
  setError?: TErrorNotification['setError']
) => {
  const formMethods = useFormContext<InstructionPdfForm>();
  const hasShownMacroErrorRef = useRef(false);

  const { watch } = formMethods || {};

  const debouncedPreviewFile = useDebouncedCallback(
    (data: InstructionPdfForm) => {
      handlePreviewFile(data);
    },
    500
  );

  const {
    mutate,
    isPending: isLoading,
    data: dataResponse,
  } = useMutation<PreviewPDFResponse, Error, PreviewPDFRequest>({
    mutationFn: (data) => generatePdf(data),
  });

  const {
    data: macroData,
    error: macroError,
    isFetching: isFetchingMacro,
  } = useGetApPayeesMacroList(apPayeeId);

  const dataMacroArray = useMemo(
    () => macroData?.data?.result ?? [],
    [macroData]
  );

  useEffect(() => {
    if (isFetchingMacro) {
      hasShownMacroErrorRef.current = false;
    }
  }, [isFetchingMacro]);

  const blobData = useMemo(() => {
    if (!macroError && dataResponse?.pdf) {
      return dataResponse?.pdf;
    }
    return EmptyPdf;
  }, [dataResponse, macroError]);

  const isShowError = useMemo(() => {
    return (
      macroError &&
      isCommonError(macroError) &&
      setError &&
      !hasShownMacroErrorRef.current
    );
  }, [macroError, setError]);

  useEffect(() => {
    if (isShowError) {
      setError!(new Error(macroError?.messages?.join('')));
      hasShownMacroErrorRef.current = true;
    }
  }, [isShowError, macroError, setError]);

  const originalBlob = useToBlob(blobData);
  const pdfUrl = useCreateObjectURLWithMemo(originalBlob);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const generateDataRequest = useCallback(
    (data: InstructionPdfForm) => {
      const today = dayjs();
      const dateStr = today.format(DATE_FORMAT_JA);
      const macroUrl = dataMacroArray.find(
        (item) => item.before === '手続きURL'
      );
      const hasQRCode = data.content.includes('${手続きURL}') && macroUrl;
      return {
        input: {
          template_id: 'invitation_template',
        },
        option: {
          landscape: false,
          context: {
            title: escapeHtml(replaceMacros(data.title, dataMacroArray)),
            sender: escapeHtml(replaceMacros(data.sender, dataMacroArray)),
            content: escapeHtml(replaceMacros(data.content, dataMacroArray)),
            destination: escapeHtml(
              replaceMacros(data.destination, dataMacroArray)
            ),
            date: dateStr,
            qrcode_link: hasQRCode ? macroUrl?.after : '',
            preview_mode: '',
          },
        },
      };
    },
    [dataMacroArray]
  );

  const handlePreviewFile = useCallback(
    (data: InstructionPdfForm) => {
      const dataRequest = generateDataRequest(data);

      mutate(dataRequest);
    },
    [generateDataRequest, mutate]
  );

  // Watch form values and trigger preview with debounce
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'saveAsTemplate') return;
      const currentData = formMethods.getValues();
      debouncedPreviewFile(currentData);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, formMethods, debouncedPreviewFile]);

  // Regenerate preview when macro data change.
  useEffect(() => {
    if (dataMacroArray && dataMacroArray.length > 0) {
      const currentData = formMethods.getValues();
      if (currentData && currentData.content) {
        handlePreviewFile(currentData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMacroArray]);

  return {
    handlePreviewFile,
    isLoading,
    pdfUrl,
  };
};
